// @ts-nocheck
import * as React from "react";
import * as V from "../api";
import { Badge, Button, Card, Empty, Loading, TOKENS, errorMessage, useToast } from "../ui";

// Phase 1 — 내 소속(비운영자) · 전체 소속 승인/해지(운영자).

export function MembersPanel({ isOperator }: { bureaus: V.Bureau[]; isOperator: boolean }) {
  const toast = useToast();
  const [mine, setMine] = React.useState<V.Member[] | null>(null);
  const [list, setList] = React.useState<V.Member[] | null>(null);
  const [status, setStatus] = React.useState<string>("");
  const [acting, setActing] = React.useState<number | null>(null);

  const reloadMine = React.useCallback(() => {
    V.members.me().then((r) => setMine(r.items)).catch(() => setMine([]));
  }, []);

  const reloadList = React.useCallback(() => {
    if (!isOperator) return;
    V.members.list({ status: status || undefined })
      .then((r) => setList(r.items))
      .catch((e) => { setList([]); toast.push(errorMessage(e), "danger"); });
  }, [isOperator, status, toast]);

  React.useEffect(() => { reloadMine(); }, [reloadMine]);
  React.useEffect(() => { reloadList(); }, [reloadList]);

  const act = async (fn: (id: number) => Promise<V.Member>, id: number, msg: string) => {
    setActing(id);
    try {
      await fn(id);
      toast.push(msg, "success");
      reloadList();
      reloadMine();
    } catch (e: any) {
      toast.push(errorMessage(e), "danger");
    } finally {
      setActing(null);
    }
  };

  return (
    <div>
      <h2 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 12px" }}>내 소속</h2>
      {mine === null ? <Loading/> :
        mine.length === 0 ? <Empty title="소속된 교육국이 없습니다." hint="7국 개요 탭에서 참여 신청할 수 있습니다." /> :
        <div style={{ display: "grid", gap: 8, marginBottom: 24 }}>
          {mine.map((m) => (
            <Card key={m.id}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>
                    {m.bureau.name}
                    <Badge tone={toneForMemberStatus(m.status)}>&nbsp;{V.LABELS.memberStatus[m.status]}</Badge>
                  </div>
                  <div style={{ fontSize: 12, color: TOKENS.ink3, marginTop: 2 }}>
                    {V.LABELS.roleInBureau[m.role_in_bureau || ""] || "일반 소속"}
                    {m.approved_at && ` · 승인: ${fmtDate(m.approved_at)}`}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      }

      {isOperator && (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "16px 0 12px" }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>전체 소속 (운영자)</h2>
            <div style={{ display: "flex", gap: 6 }}>
              {["", "pending", "active", "revoked"].map((s) => (
                <button
                  key={s || "all"}
                  onClick={() => setStatus(s)}
                  style={{
                    padding: "4px 10px", borderRadius: 999, fontSize: 12,
                    border: `1px solid ${status === s ? TOKENS.orange500 : TOKENS.border}`,
                    background: status === s ? TOKENS.orange50 : "transparent",
                    color: status === s ? TOKENS.orange600 : TOKENS.ink2,
                    cursor: "pointer",
                  }}
                >{s === "" ? "전체" : V.LABELS.memberStatus[s]}</button>
              ))}
            </div>
          </div>
          {list === null ? <Loading /> :
            list.length === 0 ? <Empty title="해당 조건의 소속이 없습니다." /> :
            <div style={{ display: "grid", gap: 8 }}>
              {list.map((m) => (
                <Card key={m.id}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700 }}>
                        {m.user.name} · {m.bureau.name}
                        &nbsp;<Badge tone={toneForMemberStatus(m.status)}>{V.LABELS.memberStatus[m.status]}</Badge>
                      </div>
                      <div style={{ fontSize: 12, color: TOKENS.ink3, marginTop: 2 }}>
                        {m.user.email} · {V.LABELS.roleInBureau[m.role_in_bureau || ""] || "일반 소속"}
                        {m.note && ` · "${m.note}"`}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      {m.status === "pending" && (
                        <Button size="sm" disabled={acting === m.id}
                          onClick={() => act(V.members.approve, m.id, `${m.user.name}님의 신청을 승인했습니다.`)}>
                          승인
                        </Button>
                      )}
                      {m.status === "active" && (
                        <Button variant="danger" size="sm" disabled={acting === m.id}
                          onClick={() => {
                            if (!window.confirm(`${m.user.name}님의 ${m.bureau.name} 소속을 해지하시겠습니까?`)) return;
                            act(V.members.revoke, m.id, "소속을 해지했습니다.");
                          }}>
                          해지
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          }
        </>
      )}
    </div>
  );
}

function toneForMemberStatus(s: string) {
  return s === "active" ? "success" : s === "pending" ? "warning" : "neutral";
}

function fmtDate(iso: string) {
  try { return new Date(iso).toLocaleDateString("ko-KR"); } catch { return iso; }
}
